<script>
  import { onMount, onDestroy } from "svelte";
  import Icon from "@iconify/svelte";
  import historyIcon from "@iconify/icons-mdi/history";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import chevronLeft from "@iconify/icons-mdi/chevron-left";
  import chevronRight from "@iconify/icons-mdi/chevron-right";
  import chevronUp from "@iconify/icons-mdi/chevron-up";
  import chevronDown from "@iconify/icons-mdi/chevron-down";
  import clockIcon from "@iconify/icons-mdi/clock-outline";
  import sortAlphaIcon from "@iconify/icons-mdi/sort-alphabetical-ascending";
  import { t } from "./lib/i18n.js";

  import WebhookDetail from "./WebhookDetail.svelte";

  const POLL_MS = 250;

  /** Portal node to document.body so fixed positioning is relative to viewport (fixes mobile overlay). */
  function portal(node) {
    document.body.appendChild(node);
    return {
      destroy() {
        if (node.parentNode) node.parentNode.removeChild(node);
      }
    };
  }

  export let apiBase = "";
  export let apiKey = "";
  export let locale = "en";
  export let setRefresh = () => {};
  export let onUnauthorized = () => {};
  export let onApiError = () => {};

  let webhooks = [];
  let total = 0;
  let offset = 0;
  let limit = 25;
  let hasMore = false;
  let loading = false;
  let error = "";
  let searchQuery = "";
  let searchInput = "";

  let selectedWebhook = null;

  /** 'time' | 'path'; direction asc/desc. Default latest first (time desc) */
  let sortBy = "time";
  let sortDir = "desc";

  $: sortedWebhooks = (() => {
    const list = [...webhooks];
    if (sortBy === "time") {
      list.sort((a, b) => {
        const tA = new Date(a.timestamp).getTime();
        const tB = new Date(b.timestamp).getTime();
        return sortDir === "desc" ? tB - tA : tA - tB;
      });
    } else {
      list.sort((a, b) => {
        const pA = (a.path || "").toLowerCase();
        const pB = (b.path || "").toLowerCase();
        const c = pA.localeCompare(pB);
        return sortDir === "asc" ? c : -c;
      });
    }
    return list;
  })();

  function authHeaders() {
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
  }

  /** @param {boolean} [silent] - If true, don't set loading (for background poll); avoids UI flash */
  async function fetchWebhooks(silent = false) {
    if (!silent) loading = true;
    if (!silent) error = "";

    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (searchQuery) params.set("q", searchQuery);
      const res = await fetch(`${apiBase}/webhooks?${params}`, {
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(t(locale, "auth_required"));
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      webhooks = data.webhooks || [];
      total = data.total || 0;
      hasMore = data.has_more || false;
    } catch (e) {
      if (!silent) {
        error = e.message;
        onApiError(e.message);
      }
    } finally {
      if (!silent) loading = false;
    }
  }

  function refresh() {
    fetchWebhooks(false);
  }

  let pollInterval = null;
  function startPolling() {
    if (pollInterval) clearInterval(pollInterval);
    pollInterval = setInterval(() => fetchWebhooks(true), POLL_MS);
  }

  function nextPage() {
    offset += limit;
    fetchWebhooks();
  }

  function prevPage() {
    offset = Math.max(0, offset - limit);
    fetchWebhooks();
  }

  function doSearch() {
    searchQuery = searchInput.trim();
    offset = 0;
    fetchWebhooks();
  }

  /** True if webhook has any common signature header (case-insensitive) */
  function hasSignature(webhook) {
    const h = webhook?.headers || {};
    return Object.entries(h).some(([k, v]) => /x-(hub-)?(webhook-)?signature(-256)?/i.test(k) && v);
  }

  function toggleSortTime() {
    if (sortBy === "time") sortDir = sortDir === "desc" ? "asc" : "desc";
    else {
      sortBy = "time";
      sortDir = "desc";
    }
  }

  function toggleSortPath() {
    if (sortBy === "path") sortDir = sortDir === "asc" ? "desc" : "asc";
    else {
      sortBy = "path";
      sortDir = "asc";
    }
  }

  onMount(() => {
    setRefresh(refresh);
    fetchWebhooks(false);
    startPolling();
  });

  onDestroy(() => {
    if (pollInterval) clearInterval(pollInterval);
  });

  function getMethodClass(method) {
    const m = method?.toLowerCase();
    return `method-${m}`;
  }

  function formatTimestamp(ts) {
    return new Date(ts).toLocaleString();
  }

  function getTimeSince(ts) {
    const seconds = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  }
</script>

<main class="w-full p-4 sm:p-6">
  <section class="ui-card p-5 sm:p-6">
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3 flex-1 min-w-0">
        <span class="inline-flex h-9 w-9 items-center justify-center rounded border border-violet-400/60 bg-violet-400/20 text-white flex-shrink-0">
          <Icon icon={historyIcon} width="18" height="18" />
        </span>
        <div class="min-w-0">
          <h1 class="text-lg font-normal tracking-wide text-zinc-100">{t(locale, "history_title")}</h1>
          <p class="mt-0.5 text-sm text-zinc-400 hidden sm:block">{t(locale, "history_subtitle")}</p>
        </div>
      </div>
      <button
        type="button"
        class="flex-shrink-0 inline-flex items-center justify-center gap-2 rounded border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] px-3 py-2 text-sm text-zinc-200 transition-colors"
        title={t(locale, "history_refresh_title")}
        onclick={refresh}
      >
        <Icon icon={refreshIcon} width="18" height="18" />
        <span class="hidden sm:inline">{t(locale, "history_refresh")}</span>
      </button>
    </div>

    <div class="mt-6 flex flex-col lg:flex-row gap-6">
      <!-- Request list -->
      <div class="flex-1 min-w-0">
        <div class="ui-card-inner">
          <div class="p-4 border-b border-white/10 flex flex-wrap items-center justify-between gap-3">
            <div class="flex items-center gap-2 flex-1 min-w-0">
              <input
                type="search"
                placeholder={t(locale, "history_search_placeholder")}
                class="flex-1 min-w-[120px] max-w-[200px] rounded border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-500 focus:border-white/20 focus:outline-none"
                bind:value={searchInput}
                onkeydown={(e) => e.key === "Enter" && doSearch()}
              />
            </div>
            <span class="text-sm text-zinc-400">{t(locale, "history_total", { count: total })}</span>
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
                onclick={toggleSortTime}
                title={sortBy === "time" && sortDir === "desc" ? t(locale, "history_sort_time_latest") : t(locale, "history_sort_time")}
              >
                <Icon icon={clockIcon} width="14" height="14" />
                {#if sortBy === "time"}
                  <Icon icon={sortDir === "desc" ? chevronUp : chevronDown} width="14" height="14" />
                {:else}
                  <span class="opacity-50">—</span>
                {/if}
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
                onclick={toggleSortPath}
                title={sortBy === "path" ? (sortDir === "asc" ? t(locale, "history_sort_path_asc") : t(locale, "history_sort_path_desc")) : t(locale, "history_sort_path")}
              >
                <Icon icon={sortAlphaIcon} width="14" height="14" />
                {#if sortBy === "path"}
                  <Icon icon={sortDir === "asc" ? chevronUp : chevronDown} width="14" height="14" />
                {:else}
                  <span class="opacity-50">—</span>
                {/if}
              </button>
            </div>
          </div>

          {#if error}
            <div class="p-4 text-rose-400 text-sm">{error}</div>
          {/if}

          <div class="max-h-[600px] overflow-y-auto">
            {#if webhooks.length === 0 && !loading}
              <div class="p-8 text-center text-zinc-500">
                {t(locale, "history_empty")}
              </div>
            {/if}

            {#each sortedWebhooks as webhook (webhook.id)}
              <div
                role="button"
                tabindex="0"
                class="w-full px-4 py-4 text-left border-b border-white/5 hover:bg-white/[0.06] transition-colors cursor-pointer {selectedWebhook?.id === webhook.id ? 'bg-white/[0.03] border-l-2 border-l-yellow-400' : ''}"
                onclick={() => selectedWebhook = webhook}
                onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectedWebhook = webhook; } }}
              >
                <div class="flex items-center gap-3">
                  <span class={`method-badge flex-shrink-0 ${getMethodClass(webhook.method)}`}>
                    {webhook.method}
                  </span>
                  <span class="font-mono text-sm text-zinc-300 truncate min-w-0 flex-1">{webhook.path}</span>
                  {#if webhook.rejected}
                    <span class="flex-shrink-0 text-[10px] uppercase tracking-wide text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5" title={t(locale, "history_rejected_title", { status: webhook.rejected_status || 500 })}>{t(locale, "history_rejected")}</span>
                  {/if}
                  {#if hasSignature(webhook)}
                    <span class="flex-shrink-0 text-[10px] uppercase tracking-wide text-zinc-500 border border-white/10 rounded px-1.5 py-0.5" title={t(locale, "history_signature_title")}>{t(locale, "history_signature")}</span>
                  {/if}
                  <span class="text-xs text-zinc-500 flex-shrink-0">{getTimeSince(webhook.timestamp)}</span>
                </div>
                <div class="mt-2 text-xs text-zinc-500">
                  {t(locale, "history_from", { ip: webhook.source_ip, size: webhook.body_size })}
                </div>
              </div>
            {/each}
          </div>

          <!-- Pagination -->
          {#if total > limit}
            <div class="p-4 border-t border-white/10 flex items-center justify-between">
              <div class="text-sm text-zinc-400">
                {t(locale, "history_showing", { start: offset + 1, end: Math.min(offset + webhooks.length, total), total })}
              </div>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="ui-btn text-sm"
                  disabled={offset <= 0}
                  onclick={prevPage}
                >
                  <Icon icon={chevronLeft} width="16" height="16" />
                </button>
                <button
                  type="button"
                  class="ui-btn text-sm"
                  disabled={!hasMore}
                  onclick={nextPage}
                >
                  <Icon icon={chevronRight} width="16" height="16" />
                </button>
              </div>
            </div>
          {/if}
        </div>
      </div>

      <!-- Desktop: inline right-hand sidebar -->
      {#if selectedWebhook}
        <div class="hidden lg:flex flex-shrink-0 w-1/2 min-h-0 flex flex-col">
          <WebhookDetail
            webhook={selectedWebhook}
            {locale}
            onClose={() => selectedWebhook = null}
          />
        </div>
      {/if}
    </div>

    <!-- Small screens only: full width and height overlay (portaled to body so fixed works on mobile) -->
    {#if selectedWebhook}
      <div
        use:portal
        class="lg:hidden fixed inset-0 z-[60] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={t(locale, "history_detail")}
      >
        <div
          class="absolute inset-0 bg-black/80"
          role="button"
          tabindex="-1"
          aria-label={t(locale, "close")}
          onclick={() => selectedWebhook = null}
          onkeydown={(e) => e.key === 'Escape' && (selectedWebhook = null)}
        ></div>
        <div class="absolute inset-0 flex flex-col bg-[rgb(8,8,8)] overflow-hidden">
          <div class="flex-1 min-h-0 flex flex-col overflow-hidden">
            <WebhookDetail
              webhook={selectedWebhook}
              {locale}
              onClose={() => selectedWebhook = null}
            />
          </div>
        </div>
      </div>
    {/if}
  </section>
</main>
